import {
  createContext,
  useReducer,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { frontendServerGateway } from '../api/api-utils/commonApiUtils';
import { userSchema } from '../api/schemas/user-profile.schema';

import type { Dispatch, ReactNode } from 'react';
import type { Session } from 'next-auth';
import type { ConsentOptions } from '../types';
import type { UserReducerAction } from '../contexts/reducers/userReducer';

import axios from 'axios';
import { userReducer } from '../contexts/reducers/userReducer';
import { useRouter } from 'next/router';
import type { SessionContextValue } from 'next-auth/react';
import { signIn, signOut, useSession } from 'next-auth/react';
import { fireToast } from '../helpers/Toasts';
import { UserProfile } from '../api/schemas/user-profile.schema';
import { setupAuthInterceptor } from '../api/api-utils/commonApiUtils';
import { isProtectedRoute } from '../constants/protectedRoutes';
import { API } from '../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';

interface UserProviderProps {
  children: ReactNode;
  initialUser: UserProfile | undefined;
  consentOptions: ConsentOptions;
}

interface UserContextI {
  userProfile: UserProfile | undefined;
  dispatchUserProfile: Dispatch<UserReducerAction>;
  consentOptions: ConsentOptions;
  isUserVerified: boolean;
  refreshUserVerification: () => Promise<void>;
  /**
   * Perform a logout.
   * @param options - The options for the logout.
   * @param options.userInitiated - Whether the logout was initiated by the user.
   * This should only be true if the user clicked the signout button in the UI.
   * @param options.callbackUrl - The URL to redirect to after logout.
   */
  performLogout: (options: {
    userInitiated: boolean;
    callbackUrl?: string;
  }) => Promise<void>;
  handleLogin: (url?: string) => void;
  isAuthenticated: boolean;
  accessToken: string | undefined;
  status: SessionContextValue['status'];
  user: Session['user'] | undefined;
}

const UserContext = createContext<UserContextI>({
  userProfile: undefined,
  dispatchUserProfile: () => null,
  consentOptions: {
    functionalConsent: false,
    allowGoogle: false,
    allowWootric: false,
    allowBraze: false,
    allowConvert: false,
  },
  isUserVerified: false,
  refreshUserVerification: () => Promise.resolve(),
  performLogout: async () => undefined,
  handleLogin: () => undefined,
  isAuthenticated: false,
  accessToken: undefined,
  status: 'unauthenticated',
  user: undefined,
});

function UserProvider({
  children,
  initialUser,
  consentOptions,
}: UserProviderProps) {
  const [userProfile, dispatchUserProfile] = useReducer(
    userReducer,
    initialUser,
  );

  const isUserVerified = Boolean(
    userProfile?.verification.email || userProfile?.verification.phone,
  );

  const { data: session, status, update: updateSession } = useSession();
  const router = useRouter();
  const asPath = router.asPath;
  const isAuthenticated = !!session?.user;
  const accessToken =
    isAuthenticated && !session?.error ? session?.accessToken : undefined;

  const handleLogin = useCallback(
    (url?: string) => {
      signIn('keycloak', {
        callbackUrl:
          typeof url === 'string'
            ? `/signin?callbackUrl=${url}`
            : `/signin?callbackUrl=${asPath}`,
      });
    },
    [asPath],
  );

  // Pin the unstable values behind refs so the interceptor setup effect runs
  // once — otherwise every session refetch would tear down and recreate the
  // interceptor, resetting its concurrent-refresh lock mid-flight.
  // Replace with useEffectEvent once we're on React 19.
  const updateSessionRef = useRef(updateSession);
  updateSessionRef.current = updateSession;

  const userProfileNameRef = useRef(userProfile?.name || '');
  userProfileNameRef.current = userProfile?.name || '';

  const performLogout = useCallback(
    async ({
      userInitiated,
      callbackUrl,
    }: {
      userInitiated: boolean;
      callbackUrl?: string;
    }) => {
      const signOutAndClear = async () => {
        await signOut(callbackUrl ? { callbackUrl } : { redirect: false });
        dispatchUserProfile({ type: 'CLEAR' });
        if (isProtectedRoute(location.pathname)) {
          location.assign('/');
        } else {
          fireToast({
            type: 'SUCCESS',
            text: `You're now logged out ${userProfileNameRef.current}. Hope to see you soon!`,
          });
        }
      };

      if (userInitiated) {
        try {
          await axios.get('/api/auth/keycloak_signout');
        } catch (error) {
          // Log and fall through — we still want to clear the local session even
          // if the keycloak signout request fails, otherwise the user is left
          // half-authenticated.
          logger.error({
            message: 'Unable to signout',
            error,
            route: API.KEYCLOAK_SIGNOUT,
          });
        }
        await signOutAndClear();
      } else {
        await runOnceAcrossTabs({
          lockName: 'dd-logout',
          callback: async () => {
            try {
              await signOutAndClear();
            } catch (error) {
              logger.error({
                message:
                  'An error occurred while trying to force the user to log out',
                error,
              });
            }
          },
        });
      }
    },
    [dispatchUserProfile],
  );

  useEffect(() => {
    setupAuthInterceptor({
      onSessionRefreshSuccess: async () => {
        // At this point, the next-auth cookie is already refreshed by the interceptor,
        // This connects the interceptor's behavior back to next-auth in the client here.
        await updateSessionRef.current();
      },
      onSessionRefreshError: async () => {
        if (isProtectedRoute(location.pathname)) {
          signIn('keycloak');
          return;
        }
        await performLogout({ userInitiated: false });
      },
    });
  }, [performLogout]);

  const inFlightRef = useRef<Promise<void> | null>(null);

  const refreshUserVerification = useCallback(async (): Promise<void> => {
    // Handle concurrent requests
    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const promise = (async () => {
      await frontendServerGateway.delete('/user-profile');
      const { data } = await frontendServerGateway.get('/user-profile');
      const freshProfile = userSchema.parse(data);
      dispatchUserProfile({ type: 'UPDATE', payload: freshProfile });
    })();

    inFlightRef.current = promise;

    try {
      await promise;
    } finally {
      inFlightRef.current = null;
    }
  }, [dispatchUserProfile]);

  const value = useMemo(
    () => ({
      dispatchUserProfile,
      performLogout,
      handleLogin,
      refreshUserVerification,
      userProfile,
      consentOptions,
      isUserVerified,
      isAuthenticated,
      accessToken,
      status,
      user: session?.user,
    }),
    [
      performLogout,
      handleLogin,
      refreshUserVerification,
      userProfile,
      consentOptions,
      isUserVerified,
      isAuthenticated,
      accessToken,
      status,
      session?.user,
    ],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

const useUserContext = () => useContext(UserContext);

// NB: This won't work in Safari <= 15.4, it will fallback to running the callback on every tab
function runOnceAcrossTabs({
  lockName,
  callback,
}: {
  lockName: string;
  callback: () => void | Promise<void>;
}) {
  if (!navigator.locks) {
    return callback();
  }

  // ifAvailable: true means that we get lock === null if the lock is already taken by another tab
  // the default behaviour being to wait for the lock to be available
  return navigator.locks.request(
    lockName,
    { ifAvailable: true },
    async (lock) => {
      if (lock === null) {
        return undefined;
      }

      return callback();
    },
  );
}

export { UserProvider, useUserContext };
